import { css } from '@emotion/react';
import { Children, forwardRef } from 'react';

import DomainManager from '../../../utils/DomainManager';
import { WeblogPageCategory, WeblogScreenName } from '../../../constants/weblog';
import { logBrazeEvent } from '@myrealtrip/marketing-log';
import { Banner } from '../../../types/globalSearch';
import { helper } from '@myrealtrip/frontend-weblog';

import styled from '@emotion/styled';
import { globalVariables } from '../../../designSystem/constants';
import { useEnvContext } from '../../../../modules/EnvProvider';
import { useLogData } from '../LogDataContext';
import SearchManager from '../SearchManager';

const { sendSearchLog } = helper;

const baseSearchLog = {
  eventName: 'gsearch',
  itemKind: 'search',
  screenName: WeblogScreenName.COMMON,
  pageCategory: WeblogPageCategory.COMMON,
};
interface Props {
  data: Banner;
  isSelected: boolean;
}

const searchManager = new SearchManager();

const SuggestionKeywordItem = forwardRef<HTMLAnchorElement, Props>(({ data, isSelected }, ref) => {
  const { targetEnv } = useEnvContext();
  const logData = useLogData();
  const { image, titles, subTitles = [], linkUrl } = data;
  const { url } = image;
  const reducer = (prev: string, current: string) => prev + current;
  const searchWord = titles.map(({ text }) => text).reduce(reducer);
  const domainManager = new DomainManager(targetEnv);

  const targetUrl =
    linkUrl ||
    `${domainManager.getTravelerServiceDomain()}/search-bridge?q=${encodeURIComponent(searchWord)}`;

  const handleClick = () => {
    searchManager.setRecentKeywords(searchWord);
    sendLog();
  };

  const sendLog = () => {
    try {
      logBrazeEvent('search_offer', {
        search_offer: searchWord,
      });

      sendSearchLog({
        ...baseSearchLog,
        searchWord,
        targetUrl,
        screenName: logData?.screenName ?? baseSearchLog.screenName,
        pageCategory: logData?.pageCategory ?? baseSearchLog.pageCategory,
        ...logData?.params,
      });
    } catch (e) {
      // ignore
    }
  };

  return (
    <Anchor
      id={searchWord}
      href={targetUrl}
      ref={ref}
      isSelected={isSelected}
      onClick={handleClick}
    >
      <Icon src={url} />
      <TitleContainer>
        {Children.toArray(titles.map(({ text, color }) => <Title color={color}>{text}</Title>))}
        {Children.toArray(
          subTitles.map(({ text, color }) => <SubTitle color={color}>{text}</SubTitle>),
        )}
      </TitleContainer>
    </Anchor>
  );
});

SuggestionKeywordItem.displayName = 'SuggestionKeywordItem';

export default SuggestionKeywordItem;

const Anchor = styled.a<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  ${({ isSelected }) =>
    isSelected &&
    css`
      background-color: ${globalVariables.gray_50};
    `};

  :hover {
    background-color: ${globalVariables.gray_50}; /* gray-50 */
  }
`;

const Icon = styled.img`
  width: 32px;
  height: 32px;
  margin-right: 12px;
  border-radius: 8px;
`;

const TitleContainer = styled.span`
  display: inline-block;
  max-width: 100%;
`;

const Title = styled.span<{ color?: string }>`
  font-size: 15px;
  color: ${({ color }) => color || globalVariables.black};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const SubTitle = styled.span<{ color?: string }>`
  flex-shrink: 0;
  margin-left: 8px;
  font-size: 12px;
  color: ${({ color }) => color || globalVariables.black};
`;
